import React, { useState, useEffect, useCallback } from 'react';
import {
  Download,
  FileSpreadsheet,
  X,
  ChevronDown,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Calendar,
  MapPin,
  Layers,
  FileText
} from 'lucide-react';
import { GET } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import TenureSelect from '../components/TenureSelect';

// --- Global Styles for Thin Scrollbar ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

const showToast = (message, type = 'info') => {
  console[type === 'error' ? 'error' : 'log'](message);
  // You can integrate your global toast notification here
};

const AttendanceReport = () => {
  const [reportData, setReportData] = useState([]);
  const [meetingDates, setMeetingDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Dropdown Data
  const [cities, setCities] = useState([]);
  const [chapters, setChapters] = useState([]);

  // Filter State
  const [dateRange, setDateRange] = useState('Last 3 Months');
  const [tenureId, setTenureId] = useState('');
  const master = useMasterChapter();
  const [selectedCity, setSelectedCity] = useState(master.cityId);
  const [selectedChapter, setSelectedChapter] = useState(master.chapterId);

  // Pagination State
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'business_name',
    direction: 'asc'
  });

  // PAGINATION HANDLERS 
  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // FETCH CITIES
  const fetchCities = useCallback(async () => {
    try {
      const data = await GET('/report/cities');
      if (data) setCities(data);
    } catch (err) {
      console.error(err);
      showToast('Failed to load cities', 'error');
    }
  }, []);

  // FETCH CHAPTERS
  const fetchChaptersByCityId = useCallback(async (cityId) => {
    if (!cityId) {
      setChapters([]);
      return;
    }
    try {
      const data = await GET(`/report/chapters/city/${cityId}`);
      if (data) setChapters(data);
    } catch (err) {
      console.error(err);
      showToast('Failed to load chapters', 'error');
    }
  }, []);

  // FETCH REPORT DATA
  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('dateRange', dateRange);
      if (tenureId) params.append('tenureId', tenureId);
      params.append('page', currentPage);
      params.append('limit', rowsPerPage);
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);

      if (selectedCity) params.append('city', selectedCity);
      if (selectedChapter) params.append('chapter', selectedChapter);

      const result = await GET(`/attendance-reports?${params}`);

      if (result && result.success) {
        setReportData(result.data || []);
        setMeetingDates(result.meeting_dates || []);
        setTotalPages(result.pagination?.total_pages || 1);
        setTotalRecords(result.pagination?.total_records || 0);
      } else {
        setReportData([]);
        setMeetingDates([]);
      }
    } catch (err) {
      console.error(err);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  // Master chapter filter (super-admin header) locks the page's city/chapter filter
  useEffect(() => {
    setSelectedCity(master.cityId);
    setSelectedChapter(master.chapterId);
    setCurrentPage(1);
  }, [master.cityId, master.chapterId]);

  useEffect(() => {
    fetchChaptersByCityId(selectedCity);
    setSelectedChapter(master.chapterId); // '' unless the master filter pins the chapter
  }, [selectedCity, fetchChaptersByCityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A sub-admin with access to just one city / chapter gets it pre-selected (also after Clear).
  const isScopedAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role !== 'super-admin'; } catch { return false; }
  })();

  useEffect(() => {
    if (isScopedAdmin && !selectedCity && cities.length === 1) setSelectedCity(cities[0]._id);
  }, [isScopedAdmin, cities, selectedCity]);

  useEffect(() => {
    if (isScopedAdmin && selectedCity && !selectedChapter && chapters.length === 1
            && (chapters[0].city_id?._id || chapters[0].city_id) === selectedCity) setSelectedChapter(chapters[0]._id);
  }, [isScopedAdmin, chapters, selectedCity, selectedChapter]);

  useEffect(() => {
    fetchAttendanceData();
  }, [dateRange, tenureId, selectedCity, selectedChapter, currentPage, rowsPerPage, sortConfig]);

  const handleClearFilters = () => {
    setDateRange('Last 3 Months');
    setTenureId('');
    setSelectedCity(master.cityId);
    setSelectedChapter(master.chapterId);
    setCurrentPage(1);
    setSortConfig({ key: 'business_name', direction: 'asc' });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
    );
  };

  const formatMeetingHeader = (dateStr) => {
    const [year, month, day] = String(dateStr).split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
      dayMonth: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
    };
  };

  const getAttendanceLabel = (status) => {
    if (status === 'P') return 'Present';
    if (status === 'A') return 'Absent';
    return 'No record';
  };

  const getInitials = (name) => {
    if (typeof name !== 'string' || !name.trim()) return 'NA';
    return name
      .trim()
      .split(/\s+/)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };

  // Export Function
  const handleDownloadReport = async (type = 'csv') => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('dateRange', dateRange);
      if (tenureId) params.append('tenureId', tenureId);
      if (selectedCity) params.append('city', selectedCity);
      if (selectedChapter) params.append('chapter', selectedChapter);
      params.append('format', type); // Optional: if your API supports format param

      const token = localStorage.getItem('authToken');
      // Adjust endpoint if needed for different formats
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/export-attendance-reports?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${new Date().toISOString().split('T')[0]}.${type === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading:", error);
      showToast("Failed to download the report", 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <CustomStyles />

      <div className="space-y-5">
        
        {/* --- Filter Card --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 animate-fade-in-up">
          
          {/* Header: Title Left, Actions Right */}
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Filter className="w-5 h-5" /></div>
              <div>
                <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">Filter Reports</h2>
                {loading && <span className="text-xs text-blue-500 font-medium flex items-center gap-1 mt-0.5"><RefreshCw className="w-3 h-3 animate-spin"/> Updating data...</span>}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              {/* Stats */}
              <div className="flex items-center gap-4 bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-100">
                <div className="text-xs font-bold text-slate-500 uppercase">Records: <span className="text-slate-800 text-sm ml-1">{totalRecords}</span></div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="text-xs font-bold text-slate-500 uppercase">Cols: <span className="text-slate-800 text-sm ml-1">{meetingDates.length + 1}</span></div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button 
                  onClick={() => handleDownloadReport('excel')} 
                  disabled={downloading}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all disabled:opacity-50 disabled:shadow-none"
                >
                  {downloading ? <RefreshCw className="w-4 h-4 animate-spin"/> : <FileSpreadsheet className="w-4 h-4" />}
                  <span>Excel</span>
                </button>
                <button 
                  onClick={() => handleDownloadReport('csv')} 
                  disabled={downloading}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-slate-300 hover:shadow-slate-400 transition-all disabled:opacity-50 disabled:shadow-none"
                >
                  {downloading ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4" />}
                  <span>CSV</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filter Inputs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-5 items-end">
            
            {/* Date Range */}
            <div className="relative">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/> Date Range</span>
              </label>
              <div className="relative">
                <select 
                  value={dateRange} 
                  disabled={Boolean(tenureId)}
                  onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1); }} 
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option>This Week</option><option>Last Week</option><option>Last 3 Months</option><option>Last 4 Months</option><option>Last 6 Months</option><option>Last 9 Months</option><option>Last 12 Months</option><option>All Time</option>
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
              </div>
              {tenureId ? (
                <p className="text-xs text-slate-400 mt-1.5 ml-1">Overridden by the selected tenure.</p>
              ) : null}
            </div>

            {/* Tenure — takes priority over Date Range */}
            <TenureSelect
              chapterId={selectedChapter}
              value={tenureId}
              onChange={(value) => { setTenureId(value); setCurrentPage(1); }}
              className="relative"
            />

            {/* City */}
            <div className="relative">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> City</span>
              </label>
              <div className="relative">
                <select 
                  value={selectedCity} 
                  onChange={(e) => { setSelectedCity(e.target.value); setCurrentPage(1); }} 
                  disabled={!!master.chapterId}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">All Cities</option>
                  {cities.map(city => <option key={city._id} value={city._id}>{city.name}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
              </div>
            </div>

            {/* Chapter */}
            <div className="relative">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                <span className="flex items-center gap-1"><Layers className="w-3 h-3"/> Chapter</span>
              </label>
              <div className="relative">
                <select 
                  value={selectedChapter} 
                  onChange={(e) => { setSelectedChapter(e.target.value); setCurrentPage(1); }} 
                  disabled={!!master.chapterId}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">All Chapters</option>
                  {chapters.map(chap => <option key={chap._id} value={chap._id}>{chap.name}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
              </div>
            </div>

            {/* Clear Button */}
            <div>
              <button 
                onClick={handleClearFilters} 
                className="w-full py-2.5 bg-white border border-red-100 text-red-500 hover:bg-red-50 hover:border-red-200 rounded-xl font-bold transition-all shadow-sm flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" /> Clear
              </button>
            </div>
          </div>
          {master.chapterId && <div className="mt-3"><MasterChapterLockNote /></div>}
        </div>

        {/* --- Report Table Card --- */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          
          {/* Card Header */}
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
               <FileText className="w-5 h-5 text-slate-400"/> Attendance List
            </h2>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3 text-xs font-semibold text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 font-bold">P</span>
                  Present
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-50 text-red-600 border border-red-100 font-bold">A</span>
                  Absent
                </span>
              </div>
              <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-500">Rows:</span>
              <select 
                value={rowsPerPage} 
                onChange={(e) => handleRowsPerPageChange(Number(e.target.value))} 
                className="p-1.5 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            </div>
          </div>

          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  {/* Fixed Columns */}
                  <th onClick={() => handleSort('name')} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-20 border-r border-slate-100 cursor-pointer hover:bg-slate-100/50 transition-colors min-w-[240px]">
                    <div className="flex items-center gap-1">Member {getSortIcon('name')}</div>
                  </th>

                  {/* Dynamic Date Columns */}
                  {meetingDates.map((date) => {
                    const header = formatMeetingHeader(date);
                    return (
                    <th key={date} className="px-3 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider min-w-[90px]">
                      <div className="flex flex-col items-center">
                        <span className="text-slate-800">{header.dayMonth}</span>
                        <span className="text-[10px] font-normal text-slate-400 mt-0.5">{header.weekday}</span>
                      </div>
                    </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading ? (
                   <tr><td colSpan={meetingDates.length + 1} className="px-6 py-10 text-center text-slate-400 font-medium">Loading attendance data...</td></tr>
                ) : reportData.length > 0 ? (
                  reportData.map((member, idx) => (
                    <tr key={member.id || idx} className="hover:bg-slate-50/80 transition-colors group">
                      {/* Member Column (Sticky) */}
                      {(() => {
                        const memberName = typeof member.name === 'string' && member.name.trim() ? member.name : 'Unknown Member';
                        return (
                      <td className="px-5 py-3 text-sm sticky left-0 bg-white group-hover:bg-slate-50 border-r border-slate-100 z-10 min-w-[240px]">
                        <div className="flex items-start">
                          {member.profile_picture ? (
                            <img
                              src={member.profile_picture}
                              alt=""
                              className="w-9 h-9 rounded-full mr-3 object-cover border border-slate-200 shadow-sm flex-shrink-0 mt-0.5"
                              onError={(e) => {
                                e.target.style.display='none';
                                e.target.nextSibling.style.display='flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-9 h-9 rounded-full mr-3 bg-blue-100 flex-shrink-0 items-center justify-center text-xs font-bold text-blue-600 shadow-sm mt-0.5"
                            style={{ display: member.profile_picture ? 'none' : 'flex' }}
                          >
                            {getInitials(memberName)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-slate-800 truncate">{memberName}</div>
                            {(member.mobile || member.business) && (
                              <div className="text-xs font-medium text-slate-500 mt-0.5 truncate">
                                {member.mobile && <span>{member.mobile}</span>}
                                {member.mobile && member.business && (
                                  <span className="mx-1.5 text-slate-300">|</span>
                                )}
                                {member.business && <span className="text-slate-600">{member.business}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                        );
                      })()}
                      
                      {/* Attendance Status Columns */}
                      {meetingDates.map((date) => {
                        const status = member.attendance && member.attendance[date];
                        return (
                          <td key={date} className="px-3 py-4 whitespace-nowrap text-center">
                            {status && status !== '-' ? (
                              <span
                                title={getAttendanceLabel(status)}
                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold border ${
                                status === 'P' 
                                  ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                  : 'bg-red-50 text-red-600 border-red-100'
                              }`}>
                                {status}
                              </span>
                            ) : (
                              <span className="text-slate-300 font-medium" title="No record">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={meetingDates.length + 1} className="px-6 py-10 text-center text-slate-400 font-medium">
                      No members found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
            <div className="text-sm font-medium text-slate-500">
              Showing <span className="font-bold text-slate-700">{reportData.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}</span> to <span className="font-bold text-slate-700">{Math.min(currentPage * rowsPerPage, totalRecords)}</span> of <span className="font-bold text-slate-700">{totalRecords}</span> entries
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1} 
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Prev</span>
              </button>
              
              <div className="hidden sm:flex items-center px-4 font-bold text-slate-700 text-sm bg-slate-50 rounded-lg border border-slate-100">
                 Page {currentPage} of {totalPages || 1}
              </div>

              <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage >= totalPages} 
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <span className="flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;